import React, { useEffect, useState, useRef, Suspense } from "react";
import FileDownload from "js-file-download";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Loader } from "@progress/kendo-react-indicators";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridNoRecords,
  GridPageChangeEvent,
  GridRowProps,
  GridSortChangeEvent,
  GridHeaderCellProps,
} from "@progress/kendo-react-grid";
import ShareDialog from "../ShareDialog";
import CallListingForMobile from "./CallListingForMobile";
import useAuth from "../../hooks/useAuth";
import useLocale from "../../hooks/useLocale";
import { CallDetail } from "../../types/call";
import { Dictionary } from "../../types/Dictionary";
import { ColorCategory } from "../../types/master-data";
import callsService from "../../services/calls.service";
import { AccessPermissionEnum } from "../../enums";
import { CallDetailTabEnum } from "../../enums/callDetailTabEnum";
import CustomUserGridInfo from "./grid/CustomUserGridInfo";
import CustomGridCellForDateTime from "./grid/CustomGridCellForDateTime";
import CustomGridCellForCallTopic from "./grid/CustomGridCellForCallTopic";
import CustomGridCellForCallOutcome from "./grid/CustomGridCellForCallOutcome";
import CustomGridCellForCallComments from "./grid/CustomGridCellForCallComments";
import CustomGridCellForCallPlayIcon from "./grid/CustomGridCellForCallPlayIcon";
import CustomGridCellForCallDirection from "./grid/CustomGridCellForCallDirection";
import CustomGridCellForDurationSeconds from "./grid/CustomGridCellForDurationSeconds";
import CustomGridCellForCallGroupedIcons from "./grid/CustomGridCellForCallGroupedIcons";
import { axiosWithAuth } from "../../utils/customAxios";
import { searchCallData } from "../../utils/callDetailUtil";
import useSwal from "../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import { Page } from "../../types/Page";
import { CallTableSortColumnEnum } from "../../enums/CallTableSortColumnEnum";
import CustomGridCellForCallTag from "./grid/CustomGridCellForCallTag";
import ParticipantPopup from "./ParticipantPopup";
import {
  Input,
  Checkbox,
  CheckboxChangeEvent,
  InputChangeEvent,
} from "@progress/kendo-react-inputs";
import useTranslation from "../../hooks/useTranslation";
import { Popup } from "@progress/kendo-react-popup";
import { Link } from "react-router-dom";
import CallDetails from "../../pages/call-details/CallDetails";
import WaveSurfer from "wavesurfer.js";
import LoadingOverlay from "../LoadingOverlay";
import { ErrorBoundary } from "react-error-boundary";
import fallbackRender from "../ErrorBoundary";
import { InsightCallDetails } from "../../types/insight";
const CallPlayer = React.lazy(() => import("./CallPlayer"));
const initialDataState = {
  skip: 0,
  take: 10,
};
let initialSort: Array<SortDescriptor> = [{ field: "callTime", dir: "desc" }];

type CommonProps = {
  isBasic?: boolean;
  loading?: boolean;
  error?: boolean;
  callData: CallDetail[] | undefined;
  handleCallFlagUpdate?: (
    selectedFlag: ColorCategory | null,
    callDetailData: CallDetail
  ) => void;
  enableServerSidePagination?: boolean;
  searchTerm: string;
  pageDetail?: Page;
  handlePaginationChange?: (pageDetail: Page) => void;
  handleSortingChange?: (sortField: string, sortDirection: string) => void;
  shared?: boolean;
};

//  When checkbox is enabled
type CheckboxEnabledProps = {
  isCheckboxEnabled: true;
  checkedRows: number[];
  setCheckedRows: React.Dispatch<React.SetStateAction<number[]>>;
  setFilteredCalls: React.Dispatch<React.SetStateAction<InsightCallDetails | undefined>>;
  handleClearAllRows: () => void;
};

// When checkbox is disabled or not provided
type CheckboxDisabledProps = {
  isCheckboxEnabled?: false;
  checkedRows?: never;
  setCheckedRows?: never;
  setFilteredCalls?: never;
  handleClearAllRows?: never;
};

type CallListingProps = CommonProps & (CheckboxEnabledProps | CheckboxDisabledProps);

const CallListing: React.FC<CallListingProps> = ({
  isBasic = false,
  loading,
  error,
  callData,
  handleCallFlagUpdate,
  enableServerSidePagination = false,
  searchTerm,
  pageDetail,
  handlePaginationChange,
  handleSortingChange,
  shared,
  isCheckboxEnabled = false,
  checkedRows,
  setCheckedRows,
  setFilteredCalls,
  handleClearAllRows
}) => {
  const trans = useTranslation("calls");
  const auth = useAuth();
  const swal = useSwal();
  const localeCtx = useLocale();
  const [page, setPage] = useState(initialDataState);
  const [sort, setSort] = React.useState<SortDescriptor[]>([]);
  const [calls, setCalls] = useState<CallDetail[] | undefined>();
  const waveform = useRef<WaveSurfer | null>(null);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["calls"]) {
      trans.fetchTranslations("calls");
    }
  }, [localeCtx?.selectedLocale]);

  
  useEffect(() => {
    setCalls(callData);
    if (enableServerSidePagination && pageDetail && pageDetail.sort) {
      let _sortField: string = "";
      switch (pageDetail.sort.sortField) {
        case CallTableSortColumnEnum.DateTime:
          _sortField = "callTime";
          break;
        case CallTableSortColumnEnum.FirstParty:
          _sortField = "firstParty.email";
          break;
        case CallTableSortColumnEnum.SecondParty:
          _sortField = "secondParty.email";
          break;
        case CallTableSortColumnEnum.Duration:
          _sortField = "callDuration";
          break;
        case CallTableSortColumnEnum.Comment:
          _sortField = "recordings[0].totalComments";
          break;
        case CallTableSortColumnEnum.Outcome:
          _sortField = "callResult.name";
          break;
        default:
          _sortField = "callTime";
          break;
      }
      setSort([
        {
          field: _sortField,
          dir: pageDetail.sort.sortDirection === "asc" ? "asc" : "desc",
        },
      ]);
    }
  }, [callData, enableServerSidePagination, pageDetail]);


  useEffect(() => {
    // keep local page in sync with incoming server pageDetail so client-side (checked-rows) view uses correct skip/take
    if (pageDetail) {
      setPage({
        skip: pageDetail.skip ?? initialDataState.skip,
        take: pageDetail.take ?? initialDataState.take,
      });
    } else {
      setPage(initialDataState);
    }
  }, [pageDetail]);

  useEffect(() => {
    setPage(initialDataState);
    // if (setCheckedRows) {
    //   setCheckedRows([]);
    // }
  }, [searchTerm]);

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (enableServerSidePagination == true && handlePaginationChange) {
      handlePaginationChange({
        ...pageDetail,
        skip: event.page.skip,
        take: event.page.take,
      });
    } else {
      if(setFilteredCalls) {
        setFilteredCalls(prev => {
          if (!prev) return prev;
          return { ...prev, page: {...page, skip: event.page.skip, take: event.page.take} };
        });
      }
      setPage(event.page);
    }

     if (setCheckedRows && checkedRows) {
      setCheckedRows([...checkedRows]);
    }

    // if (setCheckedRows && calls) {
    //   const validCheckedRows = checkedRows.filter(id =>
    //     calls.some(call =>
    //       call.recordings.some(rec => rec.id === id)
    //     )
    //   );
    //   setCheckedRows(validCheckedRows);
    // }
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    let tempSort = event.sort;
    if (event.sort.length === 0 && sort.length !== 0) {
      tempSort = sort.map((s) => {
        return { field: s.field, dir: "asc" };
      });
    }
    setSort(tempSort);
    if (enableServerSidePagination === true && handleSortingChange) {
      let _sort = tempSort[0];
      let _sortField: string = "";
      let _sortDir: string | undefined = _sort.dir;
      switch (_sort.field) {
        case "callTime":
          _sortField = CallTableSortColumnEnum.DateTime;
          break;
        case "firstParty.email":
          _sortField = CallTableSortColumnEnum.FirstParty;
          break;
        case "secondParty.email":
          _sortField = CallTableSortColumnEnum.SecondParty;
          break;
        case "callDuration":
          _sortField = CallTableSortColumnEnum.Duration;
          break;
        case "recordings[0].totalComments":
          _sortField = CallTableSortColumnEnum.Comment;
          break;
        case "callResult.name":
          _sortField = CallTableSortColumnEnum.Outcome;
          break;
        default:
          _sortField = CallTableSortColumnEnum.DateTime;
          _sortDir = "desc";
          break;
      }
      handleSortingChange(_sortField, _sortDir ?? "asc");
    }
  };

  const handleFlagSelection = async (
    selectedFlag: ColorCategory,
    callDetailData: CallDetail
  ) => {
    await callsService
      .createCallDetailCategory(
        callDetailData.platformCallDetailId,
        selectedFlag
      )
      .then((response) => {
        if (handleCallFlagUpdate)
          handleCallFlagUpdate(selectedFlag, callDetailData);
      })
      .catch(async (err) => {
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: "Error",
          text: "Failed to update call flag. Please try again.",
        };
        await swal.fire(swalOptions);
      });
  };

  const handlePlayerExpand = (callDetail: CallDetail) => {
    let newCallData = calls?.map((item: CallDetail) => {
      if (item.platformCallDetailId === callDetail.platformCallDetailId) {
        item.expanded = !callDetail.expanded;
      } else {
        item.expanded = false;
      }
      return item;
    });
    setCalls(newCallData as CallDetail[]);
  };

  const navigateToCallDetailPage = (
    callDetail: CallDetail,
    tab: CallDetailTabEnum
  ) => {
    const recordingId = callDetail.recordings[0].id;
    const routeUrl = `/calldetail/${callDetail.platformCallDetailId}?recordingId=${recordingId}&selectedtab=${tab}`;
    window.open(`${window.location.origin}${routeUrl}`);
  };

  const CustomCellForMobileView = (mobileViewProps: GridCellProps) => {
    return (
      <CallListingForMobile
        {...mobileViewProps}
        handlePlayerExpand={handlePlayerExpand}
      />
    );
  };

  const CustomCellWithPlayIcon = (playIconProps: GridCellProps) => {
    return (
      <CustomGridCellForCallPlayIcon
        {...playIconProps}
        handlePlayerExpand={handlePlayerExpand}
        fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
      />
    );
  };

  const CustomGCellWithCallParty = (callPartyprops: GridCellProps) => {
    const [visible, setVisible] = React.useState<boolean>(false);
    const toggleDialog = () => {
      setVisible(!visible);
    };
    const user =
      callPartyprops.field === "firstParty.email"
        ? callPartyprops.dataItem.firstParty
        : callPartyprops.dataItem.secondParties.find(
          (sp: any) => sp.id === 0
        ) ?? callPartyprops.dataItem.secondParties[0];
    const callDetailEnum =
      callPartyprops.field === "firstParty.email"
        ? CallDetailTabEnum.Transcript
        : CallDetailTabEnum.ParticipantHistory;

    const uniqueParticipants = callPartyprops.dataItem.secondParties.filter(
      (value: any, index: any, self: any) =>
        index == self.findIndex((obj: any) => obj.email === value.email)
    );

    return (
      <td className="uCardCol">
        <div className="moreData">
          <div
            className="cursor-pointer"
            onClick={() =>
              navigateToCallDetailPage(callPartyprops.dataItem, callDetailEnum)
            }
          >
            {callPartyprops.field === "secondParty.email" ? (
              <>
                <CustomUserGridInfo userInfo={user} />
              </>
            ) : (
              <CustomUserGridInfo userInfo={user} />
            )}
          </div>
          {callPartyprops.field === "secondParty.email" &&
            callPartyprops.dataItem.secondParties.length > 1 && (
              <div className="moreDataInr">
                <div className="moreDataList">
                  <div
                    className="moreDataListBtn"
                    style={{ cursor: "pointer" }}
                    title={trans.fetchLabelKeyTranslation(
                      "lblMoreParticipants",
                      "More Participants"
                    )}
                    onClick={toggleDialog}
                  >
                    <i className="bi bi-caret-down-fill text-black-9"></i>
                  </div>
                  {visible && (
                    <ParticipantPopup
                      toggleDialog={toggleDialog}
                      participants={uniqueParticipants}
                    ></ParticipantPopup>
                  )}
                </div>
              </div>
            )}
        </div>
      </td>
    );
  };

  const CustomCellForIconGroup = (iconGroupProps: GridCellProps) => {
    return (
      <CustomGridCellForCallGroupedIcons
        {...iconGroupProps}
        handleFlagSelection={handleFlagSelection}
        handleCallDetailNavigation={navigateToCallDetailPage}
        fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
      />
    );
  };

  const CustomCellForComments = (commentsProps: GridCellProps) => {
    return (
      <CustomGridCellForCallComments
        {...commentsProps}
        handleCallDetailNavigation={navigateToCallDetailPage}
        fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
      />
    );
  };

  const CustomCellForOutcome = (outcomeProps: GridCellProps) => {
    return (
      <CustomGridCellForCallOutcome
        {...outcomeProps}
        handleCallDetailNavigation={navigateToCallDetailPage}
      />
    );
  };

  const CustomCellForCallTag = (callTagProps: GridCellProps) => {
    return <CustomGridCellForCallTag {...callTagProps} />;
  };

  const CustomCellForReasonCode = (reasonCodeProps: GridCellProps) => {
    return (
      <td className="mx-th-tag">
        <div
          className="mx-td-spn line-height-2"
          style={{ wordBreak: "break-word" }}
        >
          {reasonCodeProps.dataItem.reasonCode?.name}
        </div>
      </td>
    );
  };

  const CustomCellWithActions = (actionProps: GridCellProps) => {
    const recordingId = actionProps.dataItem.recordings[0].id;
    const routeUrl = `/calldetail/${actionProps.dataItem.platformCallDetailId}?recordingId=${recordingId}`;
    const [downloadingRecording, setDownloadingRecording] =
      useState<Boolean>(false);
    const [shareDialogVisible, setShareDialogVisible] =
      useState<boolean>(false);
    const [downloadVideoShow, setDownloadVideoShow] = useState<boolean>(false);
    const downloadVideoAnchor = useRef<any>();
    const isDiscarded: boolean = actionProps.dataItem.status === "Discarded";
    const toggleShareDialogHandler = () => {
      setShareDialogVisible((prev) => !prev);
    };

    const onCloseShareDialog = () => {
      setShareDialogVisible(false);
    };

    const downloadRecording = async (isVideo: boolean) => {
      if (downloadingRecording) {
        return;
      }
      try {
        setDownloadingRecording(true);
        var response = await axiosWithAuth.get(
          `recording/${recordingId}/download?isVideo=${isVideo}`,
          { responseType: "arraybuffer" }
        );
        if (response.data) {
          if (isVideo) {
            FileDownload(response.data, `${recordingId}_callRecording.mp4`);
          } else {
            FileDownload(response.data, `${recordingId}_callRecording.wav`);
          }
        } else {
          const swalOptions: SweetAlertOptions = {
            icon: "error",
            title: "Error",
            text: "Recording download failed. Please try again.",
          };
          await swal.fire(swalOptions);
        }
      } catch (error) {
        console.log(error);
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: "Error",
          text: "Recording download failed. Please try again.",
        };
        await swal.fire(swalOptions);
      } finally {
        setDownloadingRecording(false);
      }
    };

    const toggleDownloadVideoShow = () => {
      setDownloadVideoShow((prevState) => !prevState);
    };

    useEffect(() => {
      if (downloadVideoShow) {
        const body = document.getElementById("root");
        const eventHandler = () => {
          setDownloadVideoShow(false);
        };
        if (body != null) {
          body.addEventListener("click", eventHandler);
        }
        return () => body?.removeEventListener("click", eventHandler);
      }
    }, [downloadVideoShow]);

    return (
      <td className="text-right">
        {shareDialogVisible && (
          <ShareDialog
            onClose={onCloseShareDialog}
            resourceObjId={actionProps.dataItem.id}
            recordingPath={routeUrl}
          />
        )}
        <Tooltip anchorElement={"target"} position={"top"} parentTitle={true}>
          <span className="iBtn iBtnDisable d-flex justify-content-end">
            <Button
              className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3
                ${actionProps.dataItem?.recordings[0]?.isAIAnalysed == false
                  ? "bg-black-2 text-black-5 border-0"
                  : "iconBtn"
                }`}
              onClick={() =>
                navigateToCallDetailPage(
                  actionProps.dataItem,
                  CallDetailTabEnum.Transcript
                )
              }
              disabled={
                actionProps.dataItem?.recordings[0]?.isAIAnalysed == false
                  ? true
                  : false
              }
            >
              <span
                className={`fs-17 line-height-1`}
                title={trans.fetchLabelKeyTranslation(
                  "BtnTranscriptTitle",
                  "Transcript"
                )}
              >
                <i className="bi bi-file-text"></i>
              </span>
            </Button>
            {downloadingRecording ? (
              <div className="radius-50 iBtn-bg text-primary d-flex align-items-center justify-content-center m-r-3">
                <Loader type={"infinite-spinner"} size="small" />
              </div>
            ) : actionProps.dataItem.isVideoAvailable ? (
              <div>
                <Button
                  onClick={toggleDownloadVideoShow}
                  className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3 ${
                    auth?.checkUserAccess(AccessPermissionEnum.DownloadCalls)
                    ? "iconBtn"
                    : "bg-black-2 text-black-5 border-0"
                    }`}
                  disabled={
                    !auth?.checkUserAccess(
                      AccessPermissionEnum.DownloadCalls
                    ) || isDiscarded
                  }
                  title={trans.fetchLabelKeyTranslation(
                    "BtnDownloadTitle",
                    "Download"
                  )}
                >
                  <span ref={downloadVideoAnchor}>
                    <i
                      className="bi bi-download fs-16"
                      style={{ lineHeight: "0" }}
                    ></i>
                  </span>
                </Button>
                <Popup
                  anchor={downloadVideoAnchor.current}
                  show={downloadVideoShow}
                  popupClass={"popup-content"}
                  style={{ margin: "10px 0 0 0" }}
                >
                  <ul className="dropMenu droplist-ul">
                    <li key="DownloadAudio" className="droplist-li">
                      <Button
                        className="droplist-a justify-content-start px-2"
                        onClick={() => {
                          downloadRecording(false);
                          setDownloadVideoShow(false);
                        }}
                        disabled={isDiscarded}
                      >
                        {trans.translationsLoading
                          ? "Download Audio"
                          : trans.fetchLabelKeyTranslation(
                            "Download Audio",
                            "Download Audio"
                          )}
                      </Button>
                    </li>
                    <li key="DownloadVideo" className="droplist-li">
                      <Button
                        className="droplist-a justify-content-start px-2"
                        onClick={() => {
                          downloadRecording(true);
                          setDownloadVideoShow(false);
                        }}
                        disabled={isDiscarded}
                      >
                        {trans.translationsLoading
                          ? "Download Video"
                          : trans.fetchLabelKeyTranslation(
                            "Download Video",
                            "Download Video"
                          )}
                      </Button>
                    </li>
                  </ul>
                </Popup>
              </div>
            ) : (
              <Button
                className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-content-center m-r-3 ${
                  auth?.checkUserAccess(AccessPermissionEnum.DownloadCalls)
                  ? "iconBtn"
                  : "bg-black-2 text-black-5 border-0"
                  }
                  `}
                onClick={() => downloadRecording(false)}
                disabled={
                  isDiscarded ||
                  !auth?.checkUserAccess(AccessPermissionEnum.DownloadCalls)
                }
              >
                <span
                  className={`fs-17 line-height-1 ${downloadingRecording && "blinkDownloadIcon"
                    }`}
                  title={
                    downloadingRecording
                      ? trans.fetchLabelKeyTranslation(
                        "BtnDownloadingTitle",
                        "Downloading..."
                      )
                      : trans.fetchLabelKeyTranslation(
                        "BtnDownloadTitle",
                        "Download"
                      )
                  }
                >
                  <i className="bi bi-download"></i>
                </span>
              </Button>
            )}
            <></>
            <Button
              className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-content-center m-r-3 ${auth?.checkUserAccess(AccessPermissionEnum.ShareCalls)
                ? "iconBtn"
                : "bg-black-2 text-black-5 border-0"
                }
                  `}
              onClick={toggleShareDialogHandler}
              disabled={!auth?.checkUserAccess(AccessPermissionEnum.ShareCalls)}
            >
              <span
                className="fs-15 line-height-1"
                title={trans.fetchLabelKeyTranslation("BtnShareTitle", "Share")}
              >
                <i className="bi bi-share"></i>
              </span>
            </Button>
          </span>
        </Tooltip>
      </td>
    );
  };

  const rowRender = (
    row: React.ReactElement<HTMLTableRowElement>,
    props: GridRowProps
  ) => {
    const expanded = props.dataItem.expanded;
    const trProps: any = {
      className: expanded ? "highlightRow" : "",
    };
    return React.cloneElement(
      row,
      { ...trProps },
      row.props.children as React.ReactNode
    );
  };

  const NotClickableHeader = (props: GridHeaderCellProps) => {
    return (
      <div>
        <span style={{ cursor: "default" }}>{props.title}</span>
      </div>
    );
  };

  const CallPlayerComponent = (props: any) => {
    localStorage.setItem("callPlayerState", "participantHistoryTab");
    return (
      <div className="ListPlayer p-b-0">
        <ErrorBoundary FallbackComponent={fallbackRender}>
          <Suspense
            fallback={
              <div>
                <LoadingOverlay
                  customStyle={{ position: "fixed", marginTop: "55px" }}
                  themeColor={"light"}
                  size={"medium"}
                  loadingText={""}
                />
              </div>
            }
          >
            <CallPlayer
              enableRegions={false}
              recordingId={props?.dataItem?.recordings[0]?.id}
              showSkipSilenceBtn={false}
              durationInSeconds={
                props?.dataItem?.recordings[0]?.recordingDurationInSeconds
              }
              wavformContainerId="participantHistoryTab"
              isDiscarded={props.dataItem.status === "Discarded"}
              waveform={waveform}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  };

  //#region Checkbox To Select Rows
  const onCheckboxChange = (event: any) => {
    const clickedCall: CallDetail = event.dataItem;
    const selectedRowId: number = event.dataItem?.recordings[0]?.id
    if (!selectedRowId) return;

    let newSelectedRows = [...(checkedRows ?? [])];
    const isAlreadySelected = newSelectedRows.includes(selectedRowId);

    if (isAlreadySelected) {
      // Deselect
        const withoutId = newSelectedRows.filter((row) => row !== selectedRowId);
      if (setCheckedRows) setCheckedRows(withoutId);
      if (setFilteredCalls) {
        setFilteredCalls(prev => {
          if (!prev) return prev;
          const remaining = prev.calls.filter(call =>
            // keep calls that do not have the clicked recording id
            !call.recordings.some(rec => rec.id === selectedRowId)
          );

          // if nothing remains
          if (remaining.length === 0) {
            handleClearAllRows();
            return undefined;
          }

          return { ...prev, calls: remaining, page: { ...prev.page, totalRecords: remaining.length } };
        });
      } 
    }
    else {
      // Select id
      newSelectedRows.push(selectedRowId);
      if (setCheckedRows) setCheckedRows(newSelectedRows);

      // Add clicked call to filteredCalls if not already present
      if (setFilteredCalls) {
        setFilteredCalls(prev => {
          // build a base if prev is undefined
          const base: InsightCallDetails = prev ?? {
            id: 'tempIdForShowingCheckedRows',
            page: {
              skip: pageDetail?.skip ?? initialDataState.skip,
              take: pageDetail?.take ?? initialDataState.take,
              totalRecords: 0,
              sort: {
                sortField: CallTableSortColumnEnum.DateTime,
                sortDirection: 'desc',
              },
            },
            calls: [],
          };

          // avoid duplicate call entries by call.id
          const exists = base.calls.some(c => c.id === clickedCall.id);
          const newCalls = exists ? base.calls : [...base.calls, clickedCall];

          return {
            ...base,
            calls: newCalls,
            page: { ...base.page, totalRecords: newCalls.length },
          };
        });
      }
    }
  };

  const onHeaderCheckboxChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.value;

    if (setCheckedRows && calls) {
      const callIds = calls.flatMap(call => call.recordings.map(r => r.id));

      if (checked) {
        setCheckedRows(prev => {
          const combined = [...prev, ...callIds];

          // Remove duplicates
          const unique = Array.from(new Set(combined));

          return unique;
        });
        const matchingCalls = calls.filter(call =>
          call.recordings.some(r => callIds.includes(r.id))
        );
        setFilteredCalls(prev => {
          if (!prev) {
            // initialize new filteredCalls structure
            return {
              id: 'tempIdForShowingCheckedRows',
              page: {
                skip: pageDetail?.skip ?? initialDataState.skip,
                take: pageDetail?.take ?? initialDataState.take,
                totalRecords: matchingCalls.length,
                sort: {
                  sortField: CallTableSortColumnEnum.DateTime,
                  sortDirection: 'desc',
                },
              },
              calls: matchingCalls,
            };
          }
           // merge with existing ones, avoiding duplicates
          const existingIds = new Set(prev.calls.map(c => c.id));
          const mergedCalls = [
            ...prev.calls,
            ...matchingCalls.filter(c => !existingIds.has(c.id)),
          ];

          return {
            id: prev.id,
            page: {
              skip: pageDetail?.skip ? pageDetail.skip : initialDataState.skip,
              take: pageDetail?.take ? pageDetail.take : initialDataState.take,
              totalRecords: mergedCalls.length,
              sort: pageDetail?.sort ? pageDetail.sort : {
                sortField: CallTableSortColumnEnum.DateTime,
                sortDirection: 'desc',
              },
            },
            calls: mergedCalls,
          }
        });
      } 
      else {
        const updatedCheckedRows = checkedRows.filter(id => !callIds.includes(id));
        setCheckedRows(updatedCheckedRows);

        if(updatedCheckedRows.length === 0) {
          handleClearAllRows();
          
          return;
        }
      }
    }
  };

  const CheckboxCell = (props: any) => {
    const { dataItem } = props;

    const isSelected = checkedRows && checkedRows.some((row) => row === dataItem?.recordings[0]?.id);

    return (
      <td className="checktext text-center">
        <Checkbox
          type="checkbox"
          checked={isSelected}
          onChange={() => onCheckboxChange({ dataItem })}
        />
      </td>
    );
  };

  const CheckboxHeader = () => {
    const allSelected =
      calls &&
      calls.length > 0 && checkedRows && checkedRows.length > 0 &&
      calls.every(call =>
        call.recordings.some(rec => checkedRows.includes(rec.id))
      )

    return (
      <Checkbox
        type="checkbox"
        checked={allSelected}
        onChange={onHeaderCheckboxChange}
        className="checktext m-t-3"
      />
    );
  };

  //#endregion

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="tableList rowSpace float-left w-100 listCalls">
          <div
            className={`tableMain gridContentOverflowY-hidden table-mobile ${shared === undefined || shared === false ? "play-icon" : ""
              }`}
          >
            <Grid
              detail={CallPlayerComponent}
              data={
                enableServerSidePagination
                  ? calls
                  : orderBy(
                    calls
                      ?.filter((el) => searchCallData(el, searchTerm))
                      .slice(page.skip, page.take + page.skip) ?? [],
                    sort
                  )
              }
              skip={enableServerSidePagination ? pageDetail?.skip : page.skip}
              take={enableServerSidePagination ? pageDetail?.take : page.take}
              total={
                enableServerSidePagination
                  ? pageDetail?.totalRecords
                  : callData?.filter((el) => searchCallData(el, searchTerm))
                    .length
              }
              pageable={{ buttonCount: 4, pageSizes: [5,10,20,50,100] }}
              onPageChange={handlePageChange}
              expandField="expanded"
              sortable={true}
              sort={sort}
              onSortChange={handleSortChange}
              rowRender={rowRender}
            >
              <GridNoRecords>
                {loading && <Loader type={"infinite-spinner"} />}
                {!loading &&
                  !error &&
                  calls?.filter((el) => searchCallData(el, searchTerm))
                    ?.length === 0 && (
                    <span>
                      {trans.fetchLabelKeyTranslation(
                        "MsgRecordsnotAvail",
                        "No Records Available"
                      )}
                    </span>
                  )}
                {error && (
                  <span className="fs-15">
                    <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                    {trans.fetchLabelKeyTranslation(
                      "ErrMsg",
                      "Uh Oh! Something Went Wrong. Please Try Again!"
                    )}
                  </span>
                )}
              </GridNoRecords>
              <GridColumn
                width={0}
                field="cellMobile"
                cell={CustomCellForMobileView}
              />

              {isCheckboxEnabled &&
                <GridColumn
                  field=""
                  width={50}
                  headerCell={CheckboxHeader}
                  cell={CheckboxCell}
                />
              }

              {(shared === undefined || shared === false) && (
                <GridColumn title="" cell={CustomCellWithPlayIcon} />
              )}
              <GridColumn
                field="callTime"
                title={trans.fetchLabelKeyTranslation(
                  "CallsGridDateTimeColLabel",
                  "Date/Time"
                )}
                cell={CustomGridCellForDateTime}
                className="line-height-2"
              // width={115}
              />

              <GridColumn
                field="firstParty.email"
                title={trans.fetchLabelKeyTranslation(
                  "CallsGridFirstPartyColLabel",
                  "Recorded User"
                )}
                cell={CustomGCellWithCallParty}
                minResizableWidth={220}
              // width={220}
              />
              <GridColumn
                title=""
                cell={CustomGridCellForCallDirection}
              // width={60}
              />
              <GridColumn
                field="secondParty.email"
                title={trans.fetchLabelKeyTranslation(
                  "CallsGridRemotePartyColLabel",
                  "Participant"
                )}
                cell={CustomGCellWithCallParty}
              // width={220}
              />
              <GridColumn
                field="callDuration"
                title={trans.fetchLabelKeyTranslation(
                  "CallsGridDurationColLabel",
                  "Duration"
                )}
                cell={CustomGridCellForDurationSeconds}
              // width={90}
              />
              <GridColumn
                title=""
                cell={CustomCellForIconGroup}
                sortable={false}
                headerCell={NotClickableHeader}
              />
              {auth?.checkTopicAvailibility() && !isBasic && (
                <GridColumn
                  field="callTopics"
                  title={trans.fetchLabelKeyTranslation(
                    "CallsGridTopicsColLabel",
                    "Topics"
                  )}
                  cell={CustomGridCellForCallTopic}
                  sortable={false}
                  headerCell={NotClickableHeader}
                // width={180}
                />
              )}
              {!isBasic && (
                <GridColumn
                  title=""
                  cell={CustomCellForComments}
                // width={110}
                />
              )}
              {!isBasic && (
                <GridColumn
                  field="callResult.name"
                  title={trans.fetchLabelKeyTranslation(
                    "CallsGridCallOutcomeColLabel",
                    "Call Outcomes"
                  )}
                  cell={CustomCellForOutcome}
                // width={160}
                />
              )}
              {!isBasic && (
                <GridColumn
                  field="reasonCode.name"
                  title={trans.fetchLabelKeyTranslation(
                    "CallsGridRCColLabel",
                    "Reason Code"
                  )}
                  cell={CustomCellForReasonCode}
                  sortable={false}
                  headerCell={NotClickableHeader}
                // width={160}
                />
              )}
              {!isBasic && (
                <GridColumn
                  field="callTag"
                  title={trans.fetchLabelKeyTranslation(
                    "CallsGridNotesColLabel",
                    "Notes"
                  )}
                  cell={CustomCellForCallTag}
                  sortable={false}
                  headerCell={NotClickableHeader}
                // width={160}
                />
              )}
              <GridColumn
                field=""
                title={trans.fetchLabelKeyTranslation(
                  "CallsGridActionsColLabel",
                  "Actions"
                )}
                sortable={false}
                cell={CustomCellWithActions}
                headerCell={NotClickableHeader}
                // width={130}
                headerClassName="text-center"
              />
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallListing;
